import { HealthIndicator, HealthIndicatorType } from '@src/data/goal'

export type ErrorMessage = {
  title: string,
  message?: string,
  fieldName?: string
}

/* TODO to be removed and replaced with isFieldFilled */
export function validateRequiredField (fieldValue: any, fieldKey?: string): ErrorMessage | undefined {
  // validate string or objects
  if ((fieldValue === undefined || fieldValue === null) || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
    return {
      title: `VALIDATION.MESSAGES.EMPTY.TITLE`,
      message: `VALIDATION.MESSAGES.EMPTY.MESSAGE`,
      fieldName: fieldKey
    }
  }
}

export function isFieldFilled (fieldValue: any): boolean {
  return (fieldValue && fieldValue.trim() !== '')
}

export function validateTrainingPlanName (name?: string): ErrorMessage | undefined {
  if (name === undefined || name.trim() === '') {
    let code = 'PAGE.CONTENT.TRAINING_PLAN.ERROR.EMPTY_TRAINING_PLAN_NAME'
    return { title: `${code}.TITLE`, message: `${code}.MESSAGE` }
  }
}

/* TODO to be removed and replaced with isEmailValid */
export function validateEmail (email: string, fieldKey?: string): ErrorMessage | undefined {
  validateRequiredField(email, fieldKey)
  return validateEmailFormat(email, fieldKey)
}

export function isEmailValid (email: string): boolean {
  return isFieldFilled(email) && isEmailFormatValid(email)
}

export function isUsernameValid (username: string): boolean {
  const regex = /^[a-zA-Z0-9_.\-@]+$/
  return regex.test(username)
}

/* TODO to be removed and replaced with isEmailFormatValid */
export function validateEmailFormat (email: string, fieldKey?: string): ErrorMessage | undefined {
  const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
  if (!regex.test(email)) {
    let code = 'PAGE.MEMBERS.CREATE.ERROR.INVALID_EMAIL'
    return { title: `${code}.TITLE`, message: `${code}.MESSAGE`, fieldName: fieldKey }
  }
}

export function isEmailFormatValid (email: string): boolean {
  const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
  return regex.test(email)
}

export function validateWorkoutName (name?: string): ErrorMessage | undefined {
  if (name === undefined || name.trim() === '') {
    let code = 'PAGE.CONTENT.WORKOUT.ERROR.EMPTY_WORKOUT_NAME'
    return { title: `${code}.TITLE`, message: `${code}.MESSAGE` }
  }
}

export function validateWorkoutType (type?: string): ErrorMessage | undefined {
  if (type === undefined || type.trim() === '') {
    let code = 'PAGE.CONTENT.WORKOUT.ERROR.EMPTY_WORKOUT_TYPE'
    return { title: `${code}.TITLE`, message: `${code}.MESSAGE` }
  }
}

export function validateExercises (exercises?: any[]): ErrorMessage | undefined {
  if (exercises === undefined || exercises.length === 0) {
    let code = 'PAGE.CONTENT.WORKOUT.ERROR.EMPTY_EXERCISE_LIST'
    return { title: `${code}.TITLE`, message: `${code}.MESSAGE` }
  }
}

export function validateMinMaxValue (value: any, min: number, max: number): boolean {
  if (isNaN(Number(value))) {
    // only validate numbers
    return true
  }

  return min <= value && value <= max
}

export function validatePassword (password: string): boolean {
  return validateMinimumLength(password, 6) && validateAtLeastOneLetter(password) && validateAtLeastOneDigit(password)
}

export function validateMinimumLength (text: string, minLength: number): boolean {
  return text.length >= minLength
}

export function validateAtLeastOneLetter (text: string): boolean {
  return new RegExp('(?=.*[a-zA-Z])').test(text)
}

export function validateAtLeastOneDigit (text: string): boolean {
  return new RegExp('(?=.*[0-9])').test(text)
}

export function validateHealthIndicator (indicator: HealthIndicator) {
  let fieldErrors: string[] = []
  switch (indicator.type) {
    case HealthIndicatorType.MET:
    case HealthIndicatorType.PIX_SCORE:
      if (validateRequiredField(indicator.target)) {
        fieldErrors.push('value')
      }
      break
    case HealthIndicatorType.EXERCISE_TAG_PERCENTAGE:
      if (indicator.target && !validateMinMaxValue(indicator.target, 0, 100)) {
        fieldErrors.push('percentage')
      }
  }
  return fieldErrors
}

export function validateFutureDate (date: Date) {
  let today = new Date()
  today.setHours(0, 0, 0, 0)
  return date >= today
}

export function validatePastDate (date: Date) {
  let today = new Date()
  today.setHours(23, 59, 59, 59)
  return date <= today
}

export function validateSupportedFileType (fileType: string, supportedTypes: string[]) {
  return supportedTypes.filter(supportedType => fileType.indexOf(supportedType) >= 0).length > 0
}

export function validateExactImageSize (imageBuffer: any, size: { width: number, height: number }): Promise<boolean> {
  return new Promise((resolve, reject) => {
    try {
      let image = new Image()
      image.onload = () => {
        resolve(image.width === size.width && image.height === size.height)
      }
      image.src = imageBuffer
    } catch (err) {
      reject(false)
    }
  })
}

export function validateExactVideoSize (videoBuffer: any, size: { width: number, height: number }): Promise<boolean> {
  return new Promise((resolve, reject) => {
    try {
      let video: HTMLVideoElement = document.createElement('video')
      video.src = videoBuffer
      video.onloadedmetadata = () => {
        resolve(video.videoWidth === size.width && video.videoHeight === size.height)
      }
    } catch (err) {
      reject(false)
    }
  })
}
